/**
 * Template save/save-as permission rules
 *
 * Determines whether a user can overwrite a template or must save as a new copy,
 * based on template scope, origin, and user role.
 */

import type { ArtefactTemplate, ArtefactTemplateScope } from '../types/artefact-templates'

// =============================================================================
// TYPES
// =============================================================================

/** User role for template permission checks */
export type TemplateUserRole = 'org_admin' | 'case_user' | 'platform_admin'

/** Save action determined by permission rules */
export type TemplateSaveAction = 'save' | 'save_as'

/** Context for determining save action */
export interface TemplateSaveContext {
  /** The template being edited */
  template: Pick<ArtefactTemplate, 'isSystem' | 'isPlatform' | 'scope' | 'caseId'>
  /** The current case ID (null if in org context) */
  currentCaseId: string | null
  /** The user's role */
  userRole: TemplateUserRole
}

// =============================================================================
// PERMISSION RULES (rules-as-data)
// =============================================================================

/**
 * Determine whether a template edit should save (overwrite) or save-as (new copy).
 *
 * Rules:
 * - System templates are always read-only → save_as
 * - Org admin can overwrite org-level templates → save
 * - Org admin must save-as for case-level templates → save_as
 * - Case user can overwrite same-case templates → save
 * - Case user must save-as for cross-case or org templates → save_as
 */
export function getTemplateSaveAction(ctx: TemplateSaveContext): TemplateSaveAction {
  const { template, currentCaseId, userRole } = ctx

  // Guard: System templates are always read-only
  if (template.isSystem) return 'save_as'

  // Platform admin can overwrite platform templates
  if (userRole === 'platform_admin') {
    if (template.scope === 'platform') return 'save'
    return 'save_as'
  }

  // Platform templates are read-only for non-platform admins
  if (template.isPlatform) return 'save_as'

  if (userRole === 'org_admin') {
    // Org admin can overwrite org-level templates
    if (template.scope === 'organisation') return 'save'
    // Org admin must save-as for case-level templates
    return 'save_as'
  }

  // Case user
  if (template.scope === 'organisation') return 'save_as'
  if (template.scope === 'case' && template.caseId === currentCaseId) return 'save'
  return 'save_as'
}

/**
 * Get the target scope for a save-as operation.
 *
 * When saving as a new template, it goes to the user's natural scope:
 * - Org admin → organisation scope
 * - Case user → case scope
 */
export function getSaveAsTargetScope(userRole: TemplateUserRole): ArtefactTemplateScope {
  if (userRole === 'platform_admin') return 'platform'
  return userRole === 'org_admin' ? 'organisation' : 'case'
}

/**
 * Generate a suggested name for a save-as copy.
 */
export function getSaveAsCopyName(originalName: string): string {
  // If already a copy, increment the number
  const copyMatch = originalName.match(/^(.+)\s\(Copy(?:\s(\d+))?\)$/)
  if (copyMatch) {
    const baseName = copyMatch[1]
    const copyNum = copyMatch[2] ? String(parseInt(copyMatch[2], 10) + 1) : '2'
    return `${baseName ?? ''} (Copy ${copyNum})`
  }
  return `${originalName} (Copy)`
}
